import { ServerMessageSchema, type ClientMessage, type ServerMessage } from "@/lib/contract";

/**
 * The sync connection, and nothing else: open it, keep it alive, reopen it
 * when it drops. It knows nothing about the replica; the engine decides what
 * each message means.
 *
 * Reconnection uses exponential backoff with jitter. Without the jitter,
 * restarting the API would bring every open tab back in the same instant, all
 * asking for catch-up deltas at once.
 */

export type CloseDecision = "retry" | "retry-now" | "stop";

export interface SocketHandlers {
  onOpen: () => void;
  onMessage: (message: ServerMessage) => void;
  /** Decide what happens after a close: try again, try again now, or give up. */
  onClose: (code: number, reason: string) => CloseDecision;
}

const BACKOFF_BASE_MS = 500;
const BACKOFF_MAX_MS = 15_000;

/**
 * An application-level heartbeat on top of the protocol-level pings the
 * server sends. Browsers answer those pings without telling the page, so a
 * half-open socket — a laptop that slept on one network and woke on another —
 * looks perfectly healthy to the page until a ping of its own goes unanswered.
 */
const PING_EVERY_MS = 25_000;
const PONG_TIMEOUT_MS = 10_000;

export class SyncSocket {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private stopped = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly url: string;
  private readonly handlers: SocketHandlers;

  constructor(url: string, handlers: SocketHandlers) {
    this.url = url;
    this.handlers = handlers;
  }

  start(): void {
    this.stopped = false;
    window.addEventListener("online", this.onOnline);
    document.addEventListener("visibilitychange", this.onVisible);
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    window.removeEventListener("online", this.onOnline);
    document.removeEventListener("visibilitychange", this.onVisible);
    this.clearTimers();
    this.ws?.close(1000, "client stopped");
    this.ws = null;
  }

  get isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /** False if the socket is not open; the caller keeps the message queued. */
  send(message: ClientMessage): boolean {
    const ws = this.ws;
    if (ws?.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify(message));
    return true;
  }

  private connect(): void {
    if (this.stopped) return;
    this.clearTimers();

    const ws = new WebSocket(this.url);
    this.ws = ws;

    ws.addEventListener("open", () => {
      if (this.ws !== ws) return;
      this.attempt = 0;
      this.startHeartbeat();
      this.handlers.onOpen();
    });

    ws.addEventListener("message", (event) => {
      if (this.ws !== ws || typeof event.data !== "string") return;
      let parsed: unknown;
      try {
        parsed = JSON.parse(event.data);
      } catch {
        console.warn("sync: unparseable message from server");
        return;
      }
      const result = ServerMessageSchema.safeParse(parsed);
      if (!result.success) {
        // A message this client cannot read means the two sides disagree about
        // the protocol. Ignore it rather than act on a guess; PROTOCOL_VERSION
        // is what turns this into a clean reload.
        console.warn("sync: message does not match the contract", result.error.issues);
        return;
      }
      if (result.data.type === "pong") {
        this.clearPongTimer();
        return;
      }
      this.handlers.onMessage(result.data);
    });

    ws.addEventListener("close", (event) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.clearTimers();
      if (this.stopped) return;

      const decision = this.handlers.onClose(event.code, event.reason);
      if (decision === "stop") return;
      this.scheduleReconnect(decision === "retry-now");
    });
  }

  private scheduleReconnect(immediately: boolean): void {
    if (this.stopped || this.reconnectTimer) return;
    const backoff = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** this.attempt);
    const delay = immediately ? 0 : backoff / 2 + Math.random() * (backoff / 2);
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private readonly onOnline = (): void => {
    if (!this.isOpen && !this.stopped) {
      this.clearReconnectTimer();
      this.attempt = 0;
      this.connect();
    }
  };

  /** A tab brought back to the front after hours asleep reconnects at once. */
  private readonly onVisible = (): void => {
    if (document.visibilityState === "visible") this.onOnline();
  };

  private startHeartbeat(): void {
    this.pingTimer = setInterval(() => {
      if (!this.send({ type: "ping" })) return;
      this.clearPongTimer();
      this.pongTimer = setTimeout(() => {
        // No answer: the connection is dead even though it looks open.
        this.ws?.close(4000, "heartbeat timeout");
      }, PONG_TIMEOUT_MS);
    }, PING_EVERY_MS);
  }

  private clearPongTimer(): void {
    if (this.pongTimer) clearTimeout(this.pongTimer);
    this.pongTimer = null;
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private clearTimers(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
    this.clearPongTimer();
    this.clearReconnectTimer();
  }
}
